import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    Account,
    AccountParams,
    AccountWithPage,
    AddAccountData,
    EnableData,
    SkillData,
    UpdatePassword,
} from '@app/interfaces/sys/account';
import { SelectOption } from '@app/interfaces/sys/dict';
import { environment } from '@env/environment';
import { lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AccountService {
    private moduleApi = environment.api.services.sys;

    private baseUrl = this.moduleApi + '/users';

    constructor(private http: HttpClient) {}

    async find(params?: AccountParams): Promise<AccountWithPage> {
        return lastValueFrom(this.http.get<AccountWithPage>(`${this.baseUrl}`, {
            params: params ? Object.assign(params) : null,
        }));
    }

    async findList(params?: AccountParams): Promise<SelectOption[]> {
        return lastValueFrom(this.http.get<SelectOption[]>(`${this.baseUrl}/all-options`, {
            params: params ? Object.assign(params) : null,
        }));
    }

    async add(data: AddAccountData): Promise<Account> {
        return lastValueFrom(this.http.post<Account>(`${this.baseUrl}`, data));
    }

    async edit(data: Account): Promise<Account> {
        return lastValueFrom(this.http.put<Account>(`${this.baseUrl}`, data));
    }

    async detail(id: number|string): Promise<Account> {
        return lastValueFrom(this.http.get<Account>(`${this.baseUrl}/one`, {
            params: Object.assign({ id }),
        }));
    }

    async resetPassword(id: number): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/password?id=${id}`));
    }

    async remove(id: number): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}`, {
            params: Object.assign({ id }),
        }));
    }

    async enable(data: EnableData): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/enable`, data));
    }

    async addSkill(data: SkillData): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 200);
        });
    }

    async removeSkill(data: SkillData): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 200);
        });
    }

    async updatePassword(data: UpdatePassword): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/password`, data));
    }

    formatAccountData(options: SelectOption[]): {
        [userId: number]: string
    } {
        let data = {};
        options.forEach(it => {
            const { value, label } = it;
            data = {
                ...data,
                [value]: label
            };
        });
        return data;
    }

    formatAccountList(options: Account[]): {
        [userId: number]: Account
    } {
        let data = {};
        options.forEach(it => {
            const { id } = it;
            data = {
                ...data,
                [id]: {
                    ...it,
                    fullName: this.getUserName(it, false),
                    fullNameWithEmail: this.getUserName(it, true)
                }
            };
        });
        return data;
    }

    getAccountData(accountStr: string): {
        name: string,
        email: string
    } {
        const emailRegex = /\(([^()]*)\)[^()]*$/; // 匹配最后一对括号及其中的内容
        const emailArr = accountStr.match(emailRegex);

        return {
            email: emailArr && emailArr.length > 0 ? emailArr[1] : '',
            name: accountStr.replace(/\s*\([^()]*\)\s*$/, '')
        };
    }

    formatUserInfo(accountList: Account[], userId: string|number): Account {
        if (!userId) {
            return {} as Account;
        }
        const account = accountList.find(it => userId.toString() === it.id.toString()) || {} as Account
        return {
            ...account,
            name: this.getUserName(account, false)
        };
    }

    getUserName(user: Account, showEmail: boolean = true): string {
        const {name, lastName, email} = user;
        const emailStr = showEmail ? ` (${email})` : '';
        return `${name} ${lastName}${emailStr}`;
    }

    formatAccount(accountList: Account[], showEmail: boolean = true): SelectOption[] {
        return accountList.map(it => {
            const { id } = it;
            return {
                value: id,
                label: this.getUserName(it, showEmail),
                email: it.email,
                avatar: it?.avatar,
                enable: it?.enable,
            };
        });
    }
}
